import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const ACTION_LABELS = {
  login: 'Signed in',
  logout: 'Signed out',
  logout_everywhere: 'Signed out all sessions',
  submit_changes: 'Submitted payroll changes',
  create_employee: 'Added new employee',
  update_salary: 'Updated salary',
  add_pending_change: 'Added pending change',
  undo_pending_change: 'Removed pending change',
  DELETE_PENDING_CHANGE: 'Removed pending change',
  delete_pending_change: 'Removed pending change',
  create_pending_change: 'Added pending change',
  upload_document: 'Uploaded document',
  upload_documents: 'Uploaded documents',
  export_csv: 'Exported CSV',
  switch_company: 'Switched company',
  password_change: 'Changed password',
  enable_2fa: 'Enabled two-factor authentication',
  disable_2fa: 'Disabled two-factor authentication',
  view_employees: 'Viewed employees',
};

const ACTION_CATEGORY = {
  login: 'session', logout: 'session', logout_everywhere: 'session',
  password_change: 'auth', enable_2fa: 'auth', disable_2fa: 'auth',
  submit_changes: 'submission',
  add_pending_change: 'submission', create_pending_change: 'submission',
  delete_pending_change: 'submission', DELETE_PENDING_CHANGE: 'submission',
  undo_pending_change: 'submission',
};

function categoryOf(action) { return ACTION_CATEGORY[action] || 'admin'; }

const CATEGORY_DOT = {
  session: 'bg-gray-400',
  submission: 'bg-emerald-500',
  admin: 'bg-brand-500',
  email: 'bg-sky-500',
  auth: 'bg-amber-500',
};

function actionLabel(action) {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function initialsOf(a) {
  if (a.first_name || a.last_name) {
    return `${(a.first_name || '')[0] || ''}${(a.last_name || '')[0] || ''}`.toUpperCase() || '·';
  }
  return '·';
}

function userName(a) {
  if (a.first_name) return `${a.first_name} ${a.last_name || ''}`.trim();
  return 'A team member';
}

function fmtFullDate(d) {
  return new Date(d).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(d) {
  return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function ActivityPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/submissions/activity')
      .then((res) => setActivities(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Split into sessions on 'login' events.
  const sessions = [];
  let current = [];
  activities.forEach((a, i) => {
    if (a.action === 'login' && i > 0) {
      if (current.length > 0) sessions.push(current);
      current = [];
    }
    current.push(a);
  });
  if (current.length > 0) sessions.push(current);

  return (
    <>
      <header className="mb-6">
        <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Activity Log</h1>
        <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2">Recent activity, grouped by session.</p>
      </header>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400">No activity recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((group, gi) => {
            const opener = group.find((a) => a.action === 'login') || group[0];
            const name = userName(opener);
            const initials = initialsOf(opener);
            const start = opener.created_at;
            return (
              <div key={gi} className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-brand-50 to-transparent dark:from-brand-900/30 border-b border-gray-100 dark:border-gray-700 px-5 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="grid place-items-center shrink-0 w-7 h-7 rounded-full bg-brand-100 text-brand-700 text-[11.5px] font-semibold">
                      {initials}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-semibold text-ink dark:text-gray-100 truncate">{name}</p>
                      <p className="text-[11.5px] text-gray-500 dark:text-gray-400">Session started {fmtFullDate(start)}</p>
                    </div>
                  </div>
                  <span className="text-[11.5px] text-gray-500 dark:text-gray-400 shrink-0">
                    <strong className="text-gray-700 dark:text-gray-200 tabular">{group.length}</strong> action{group.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ul>
                  {group.map((a, i) => {
                    const dot = CATEGORY_DOT[categoryOf(a.action)] || CATEGORY_DOT.admin;
                    return (
                      <li key={i} className="px-5 py-2.5 border-b border-gray-50 dark:border-gray-700/40 last:border-0 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                          <span className="text-[13.5px] text-gray-800 dark:text-gray-100 truncate">{actionLabel(a.action)}</span>
                          {a.metadata?.change_type && (
                            <span className="text-[12px] text-gray-500 dark:text-gray-400 truncate">
                              ({a.metadata.change_type.replace(/_/g, ' ')})
                            </span>
                          )}
                          {a.metadata?.employee_name && (
                            <span className="text-[12px] text-gray-500 dark:text-gray-400 truncate">— {a.metadata.employee_name}</span>
                          )}
                        </div>
                        <span className="text-[11.5px] tabular text-gray-400 shrink-0 whitespace-nowrap">{fmtTime(a.created_at)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
